import { RoutingControl } from "mobility-toolbox-js/ol";
import { Feature } from "ol";
import BaseEvent from "ol/events/Event";
import { GeoJSONLineString } from "ol/format/GeoJSON";
import { LineString } from "ol/geom";
import { toLonLat } from "ol/proj";

import type Control from "ol/control/Control";
import type { Coordinate } from "ol/coordinate";
import type { SimpleGeometry } from "ol/geom";

// Not exported by the package's exports map, so derive it from the constructor.
type RoutingControlOptions = NonNullable<
  ConstructorParameters<typeof RoutingControl>[0]
>;

export const GraphhopperRoutingProfile = {
  Bike: "bike",
  Car: "car",
  Foot: "foot",
} as const;

export type GraphhopperRoutingProfile =
  (typeof GraphhopperRoutingProfile)[keyof typeof GraphhopperRoutingProfile];

const STOP_FETCH_ABORT_CONTROLLER_KEY = "stop-fetch";

const getFlatCoordinatesFromSegments = (
  segmentArray: Feature[],
): Coordinate[] => {
  const coords: Coordinate[] = [];
  segmentArray.forEach((seg) => {
    const coordArr = (
      seg.getGeometry() as SimpleGeometry
    )?.getCoordinates() as Coordinate[];
    if (coordArr?.length) {
      coords.push(...coordArr);
    }
  });
  return coords;
};

/**
 * This OpenLayers control allows the user to add and modifiy via points to
 * a map and request a route from the [GraphHopper Routing API](https://docs.graphhopper.com/openapi/routing).
 */
export type GraphhopperRoutingControlOptions = {
  maxNbViaPoints?: number;
  mot?: GraphhopperRoutingProfile;
} & Omit<RoutingControlOptions, "mot">;

/** Dispatched when an insertion is refused because maxNbViaPoints is reached. */
export const MAX_NB_VIA_POINTS_EVENT = "maxnbviapoints";

// Base class with `mot` stripped from its type so the subclass can retype it
// with GraphHopper profiles instead of RoutingMot. Type-level only.
// Intersecting with Control keeps the nominal Control hierarchy (private
// members included) so instances are accepted by map.addControl/removeControl.
// `addViaPoint` and `drawRoute` are omitted so the subclass can redefine them
// as methods (Omit turns methods into properties, which TS refuses to override).
const RoutingControlBase = RoutingControl as unknown as new (
  _options?: GraphhopperRoutingControlOptions,
) => Control & Omit<RoutingControl, "addViaPoint" | "drawRoute" | "mot">;

class GraphhopperRoutingControl extends RoutingControlBase {
  maxNbViaPoints: number;

  declare mot: GraphhopperRoutingProfile;

  constructor(options?: GraphhopperRoutingControlOptions) {
    super(options);
    this.maxNbViaPoints = options?.maxNbViaPoints ?? Infinity;
  }

  addViaPoint(
    coordinatesOrString: Parameters<RoutingControl["addViaPoint"]>[0],
    index = -1,
    overwrite = 0,
  ) {
    // Only insertions count towards the limit, replacements stay allowed
    if (!overwrite && this.viaPoints.length >= this.maxNbViaPoints) {
      this.restoreRouteBeforeDrag();
      this.dispatchEvent(new BaseEvent(MAX_NB_VIA_POINTS_EVENT));
      return;
    }
    RoutingControl.prototype.addViaPoint.call(
      this,
      coordinatesOrString,
      index,
      overwrite,
    );
  }

  drawRoute(): null | Promise<void> {
    this.abortRequests();
    this.routingLayer?.getSource()?.clear();

    if (!this.viaPoints.length) {
      return null;
    }

    if (this.viaPoints.length === 1) {
      // Add point for first node
      return this.drawViaPoint(
        this.viaPoints[0],
        0,
        this.abortControllers[STOP_FETCH_ABORT_CONTROLLER_KEY],
      );
    }

    const formattedViaPoints = this.viaPoints.map((viaPoint) => {
      if (Array.isArray(viaPoint)) {
        const projection = this.getMap()?.getView().getProjection();
        // viaPoint is a coordinate
        // Coordinates need to be reversed as required by the backend RoutingAPI
        const [lon, lat] = toLonLat(viaPoint, projection);
        return this.snapToClosestStation ? [`@${lat}`, lon] : [lat, lon];
      }

      // viaPoint is a string to use as it is
      return this.useRawViaPoints ? viaPoint : `!${viaPoint}`;
    });

    this.loading = true;

    // Create point features for the viaPoints
    this.viaPoints.forEach((viaPoint, idx) => {
      void this.drawViaPoint(
        viaPoint,
        idx,
        this.abortControllers[STOP_FETCH_ABORT_CONTROLLER_KEY],
      );
    });

    const searchParams = new URLSearchParams({
      key: this.apiKey,
      points_encoded: "false",
      profile: this.mot,
    });
    formattedViaPoints.map((viaPoint) => {
      searchParams.append("point", `${viaPoint}`);
    });

    // Docs: https://docs.graphhopper.com/openapi/routing/getroute
    return fetch(
      `https://graphhopper.com/api/1/route/?${searchParams.toString()}`,
    )
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        // Throw backend error message
        return response.json().then(({ message }) => {
          throw new Error(message);
        });
      })
      .then(
        (data: {
          paths: {
            distance: number;
            points: GeoJSONLineString;
          }[];
        }) => {
          this.segments = data.paths.map((path) => {
            return this.format.readFeature(path.points) as Feature<LineString>;
          });
          // Create the new route. This route will be modifiable by the Modifiy interaction.
          const coords = getFlatCoordinatesFromSegments(this.segments);

          const routeFeature = new Feature({
            geometry: new LineString(coords),
          });
          routeFeature.set("graph", "osm");
          routeFeature.set("mot", this.mot);

          this.routingLayer?.getSource()?.addFeature(routeFeature);
          this.loading = false;
        },
      )
      .catch((error) => {
        if (/AbortError/.test(error?.name)) {
          // Ignore abort error
          return;
        }
        // We draw bird lines if request failed there is some useable viapoint
        this.segments = [];
        for (let i = 0; i < this.viaPoints.length - 1; i += 1) {
          const start = this.viaPoints[i];
          const end = this.viaPoints[i + 1];
          if (
            Array.isArray(start) &&
            Array.isArray(end) &&
            Number.isFinite(start[0]) &&
            Number.isFinite(start[1]) &&
            Number.isFinite(end[0]) &&
            Number.isFinite(end[1])
          ) {
            this.segments.push(new Feature(new LineString([start, end])));
          }
        }
        // Create the new route. This route will be modifiable by the Modifiy interaction.
        const coords = getFlatCoordinatesFromSegments(this.segments);

        const routeFeature = new Feature({
          geometry: new LineString(coords),
        });
        routeFeature.set("graph", "osm");
        routeFeature.set("mot", this.mot);
        const source = this.routingLayer?.getSource();
        source
          ?.getFeatures()
          .filter((feat) => {
            return feat.getGeometry()?.getType() === "LineString";
          })
          .forEach((feat) => {
            return source.removeFeature(feat);
          });
        this.routingLayer?.getSource()?.addFeature(routeFeature);
        // Dispatch error event and execute error function
        this.dispatchEvent(new BaseEvent("error"));

        this.onRouteError(error, this as unknown as RoutingControl);
        this.loading = false;
      });
  }

  /**
   * Resets the route geometry modified by the Modify interaction to its
   * pre-drag state (stored by onModifyStart), used when the insertion of the
   * dragged via point is refused.
   */
  restoreRouteBeforeDrag() {
    const oldGeometry = this.initialRouteDrag?.oldRoute?.getGeometry();
    if (!oldGeometry) {
      return;
    }
    const route = this.routingLayer
      ?.getSource()
      ?.getFeatures()
      .find((feat) => {
        return feat.getGeometry()?.getType() === "LineString";
      });
    route?.setGeometry(oldGeometry.clone());
  }
}

export default GraphhopperRoutingControl;
